import { useEffect } from "react";
import type { CClassEnum, CustomClassModalData, ILightingClassConfig, ILightingClassConfigCallBack, MClassEnum, PClassEnum } from "types";
import { setObjectURL, setFromURLObject } from "utils/urls";
import useCustomClassModal from "hooks/class-modal";
import { classTargetMap } from "../const/classTargetMap";
import { resolveClassByRoad } from "../utils/resolveClassByRoad";

type RoadTarget = "main" | "rightBorder" | "leftBorder";
type ClassValue = MClassEnum | CClassEnum | PClassEnum | CustomClassModalData;

export const useLightingClassConfig = (lightingClassConfig: ILightingClassConfig, onLightingClassConfig: ILightingClassConfigCallBack) => {
  const { road, isOpen, customClassData, preSelectedClass, setPreSelectedClass, updateRoad, closeModal, openModal, setCustomClassData } = useCustomClassModal();

  const { roadClasses, borderAreaRightClasses, borderAreaLeftClasses, customClasses } = lightingClassConfig;

  // biome-ignore lint/correctness/useExhaustiveDependencies: setCustomClassData identity is stable
  useEffect(() => {
    setCustomClassData((customClasses ?? {}) as CustomClassModalData);
  }, [customClasses]);

  useEffect(() => {
    setPreSelectedClass(resolveClassByRoad(road, roadClasses, borderAreaLeftClasses, borderAreaRightClasses));
  }, [road, roadClasses, borderAreaLeftClasses, borderAreaRightClasses, setPreSelectedClass, lightingClassConfig, preSelectedClass, isOpen]);

  const handleLightingClassConfig = (classType: string, value: ClassValue) => {
    const config: ILightingClassConfig = { ...lightingClassConfig };
    const targetConfig = classTargetMap[classType];

    if (targetConfig) {
      const { target, customTarget } = targetConfig;
      if (value !== config[target] && config.customClasses) {
        config.customClasses[customTarget] = undefined;
      }
      // @ts-ignore: value type is narrowed by classTargetMap lookup
      config[target] = value;
    } else if (classType === "CustomClasses") {
      config.customClasses = value as CustomClassModalData;
    } else {
      setFromURLObject(config);
      return;
    }

    setObjectURL(config);
    onLightingClassConfig(config);
    if (config.customClasses) {
      setCustomClassData(config.customClasses as CustomClassModalData);
    }
  };

  const openModalForSpecificRoad = (road: RoadTarget): void => {
    updateRoad(road);
    openModal();
  };

  const handleSaveDataFromModal = (road: RoadTarget, data: CustomClassModalData) => {
    updateRoad(road);
    setCustomClassData(data);
    handleLightingClassConfig("CustomClasses", data);
    closeModal();
  };

  return {
    isOpen,
    road,
    customClassData,
    preSelectedClass,
    closeModal,
    handleLightingClassConfig,
    openModalForSpecificRoad,
    handleSaveDataFromModal,
  };
};
